import {
  ButtonERP,
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { useContactosById } from "api/Contacto.hook"
import { GetFacturasAceptablesDtoProps } from "api/DTOs/GetFacturasAceptablesDto"
import { GetPagaresAceptablesDtoProps } from "api/DTOs/GetPagaresAceptablesDto"
import {
  AceptablesQueryParamsProps,
  getFacturasAceptables,
} from "api/Factura.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { getPagaresAceptables } from "api/Pagare.service"
import { ESTADO_VERIFICACION_EFECTO } from "api/Types/EstadoVerificacionEfecto"
import { useValidacionesPendientesUsuario } from "api/Usuario.hook"
import axios from "axios"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import SecondaryValidacion from "features/Validaciones/components/SecondaryValidation"
import { TabValidaciones } from "features/Validaciones/components/TabValidaciones/TabValidaciones"
import { VALIDATION_REDIRECTION_ORIGINS } from "features/Validaciones/constants"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { ModalAceptarFinanciacion } from "./components/ModalAuthFinanciacion"
import {
  ESTADO_TIPO_FINANCIACION,
  FINANCIACION_TYPE,
  FinanciacionType,
} from "./constant"
import { FINANCIACION_COLUMNS } from "./constants/FinanciacionColumns"
import FilterMenu from "./FilterMenu"

const Financiacion = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const isContactoPage = pathname.includes(ROUTES.CONTACTOS)
  const { contactoById } = useContactosById(id)

  const { usuarioValidacionesPendienteResponse } =
    useValidacionesPendientesUsuario()

  const [tabSelected, setTabSelected] = useState<FinanciacionType>(
    location?.state?.selectedTab ??
      (isContactoPage &&
      usuarioValidacionesPendienteResponse?.hayPagares &&
      !usuarioValidacionesPendienteResponse.hayFacturas
        ? FINANCIACION_TYPE.PAGARES
        : FINANCIACION_TYPE.FACTURAS)
  )

  const facturasCancelToken = useRef(axios.CancelToken.source())
  const pagareCancelToken = useRef(axios.CancelToken.source())
  const fetchItems = useCallback(() => {
    return (queryState: AceptablesQueryParamsProps) => {
      const queryParams = isContactoPage
        ? {
            ...queryState,
            SortingCriteria:
              queryState.SortingCriteria ?? "Verificacion.estado ASC",
            EmpresaId: contactoById?.empresaContactoId ?? "",
          }
        : { ...queryState }

      if (tabSelected === FINANCIACION_TYPE.FACTURAS) {
        facturasCancelToken.current?.cancel()
        facturasCancelToken.current = axios.CancelToken.source()
        return getFacturasAceptables(
          queryParams,
          facturasCancelToken.current.token
        )
      }
      if (tabSelected === FINANCIACION_TYPE.PAGARES) {
        pagareCancelToken.current?.cancel()
        pagareCancelToken.current = axios.CancelToken.source()
        return getPagaresAceptables(
          queryParams,
          pagareCancelToken.current.token
        )
      }
      return null
    }
  }, [contactoById?.empresaContactoId, isContactoPage, tabSelected])

  const { items, isLoading, totalCount, setServiceQuery } = useFilterMenu({
    serviceCall: fetchItems,
  })

  const [selection, setSelection] = useState<
    GetFacturasAceptablesDtoProps[] | GetPagaresAceptablesDtoProps[]
  >([])
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })

  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const dtRef =
    useRef<
      DataTablePrimeReact<
        GetFacturasAceptablesDtoProps[] | GetPagaresAceptablesDtoProps[]
      >
    >(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  const handleRedirectToDetail = (
    data: GetFacturasAceptablesDtoProps[] | GetPagaresAceptablesDtoProps[]
  ): void => {
    history.push({
      pathname: ROUTES.VALIDACIONES_FINANCIACION_DETAIL(data[0].id),
      state: {
        selectedTab: tabSelected,
        validacionesSelected: data,
        ...(isContactoPage && {
          from: VALIDATION_REDIRECTION_ORIGINS.CONTACTOS,
        }),
      },
    })
  }

  const handleTab = (value: FinanciacionType) => {
    setTabSelected(value)
    handleResetPagination()
  }

  return (
    <div
      className={`overflow-hidden tw-bg-white ${
        !isContactoPage ? "!tw-h-[calc(100vh-64px)]" : "tw-h-full"
      } tw-text-base tw-font-medium`}
    >
      {!isContactoPage && <TabValidaciones />}
      <div
        className={`overflow-hidden tw-bg-white ${
          isContactoPage ? "tw-h-full" : "tw-h-[calc(100%-60px)]"
        }`}
      >
        <div className='!tw-px-4 sm:!tw-px-8 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-center tw-gap-2 tw-py-2.5 tw-shadow-3xl tw-rounded-t-xl'>
          <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap'>
            {t("validaciones.financiable.title")} (
            {isLoading ? (
              <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
            ) : (
              <span>{totalCount}</span>
            )}
            )
          </div>
          <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-start sm:tw-flex-wrap tw-gap-1 lg:tw-gap-3'>
            <FilterMenu
              serviceQuery={setServiceQuery}
              sortingCriteria={sortingCriteria}
              maxResultCount={pagination.itemsPerPage}
              skipCount={pagination.skipCount}
              isContactoPage={isContactoPage}
              resetPagination={handleResetPagination}
            />
            <span className='tw-w-full md:tw-w-fit tw-relative'>
              <div className='tw-grid tw-grid-cols-2 tw-gap-1 tw-h-10 tw-w-auto tw-p-1 tw-bg-neutral-10 tw-rounded tw-cursor-pointer !tw-text-sm'>
                <span
                  key='facturas'
                  className={`tw-px-6 tw-flex tw-items-center tw-h-8 tw-font-medium tw-rounded tw-text-nowrap ${
                    tabSelected === ESTADO_TIPO_FINANCIACION(t)[1].value
                      ? "tw-bg-white tw-shadow"
                      : ""
                  } `}
                  onClick={() => {
                    handleTab(ESTADO_TIPO_FINANCIACION(t)[1].value)
                  }}
                >
                  <span className='tw-relative'>
                    {`${ESTADO_TIPO_FINANCIACION(t)[1].text}`}
                    {!isContactoPage &&
                      usuarioValidacionesPendienteResponse?.hayFacturas && (
                        <span className='tw-absolute tw-top-0 tw--right-2.5 tw-flex tw-h-2 tw-w-2'>
                          <span className='tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-warning tw-opacity-75' />
                          <span className='tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-warning' />
                        </span>
                      )}
                  </span>
                </span>

                <span
                  key='pagares'
                  className={`tw-px-6 tw-flex tw-items-center tw-h-8 tw-font-medium tw-rounded tw-text-nowrap ${
                    tabSelected === ESTADO_TIPO_FINANCIACION(t)[2].value
                      ? "tw-bg-white tw-shadow"
                      : ""
                  }`}
                  onClick={() => {
                    handleTab(ESTADO_TIPO_FINANCIACION(t)[2].value)
                  }}
                >
                  <span className='tw-relative'>
                    {`${ESTADO_TIPO_FINANCIACION(t)[2].text}`}
                    {!isContactoPage &&
                      usuarioValidacionesPendienteResponse?.hayPagares && (
                        <span className='tw-absolute tw-top-0 tw--right-2.5 tw-flex tw-h-2 tw-w-2'>
                          <span className='tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-warning tw-opacity-75' />
                          <span className='tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-warning' />
                        </span>
                      )}
                  </span>
                </span>
              </div>
            </span>
            <ButtonERP
              customize={{
                size: "md",
                type: "secondary",
              }}
              onClick={() => {
                handleRedirectToDetail(selection)
              }}
              disabled={!selection.length}
              label={`${t("validaciones.financiable.buttonLabel")}`}
            />
          </div>
        </div>

        <div className='tw-flex tw-flex-col tw-h-[calc(100%-60px)] tw-relative'>
          <SecondaryValidacion section='cesiones'>
            <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
              <TableERP
                refTable={dtRef}
                columns={FINANCIACION_COLUMNS({
                  t,
                  handleRedirectToDetail,
                  isContactoPage,
                })}
                className='tw-h-full'
                columnResizeMode='expand'
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
                removableSort
                value={items}
                selection={selection}
                selectionMode='checkbox'
                selectionColumnMode='multiple'
                showGridlines
                dataKey='id'
                lazy
                scrollable
                isDataSelectable={({
                  data,
                }: {
                  data:
                    | GetFacturasAceptablesDtoProps
                    | GetPagaresAceptablesDtoProps
                }) =>
                  ESTADO_VERIFICACION_EFECTO.PENDIENTE === data.estado ||
                  ESTADO_VERIFICACION_EFECTO.COMUNICADO === data.estado
                }
                rowClassName={(
                  data:
                    | GetFacturasAceptablesDtoProps
                    | GetPagaresAceptablesDtoProps
                ) =>
                  ESTADO_VERIFICACION_EFECTO.PENDIENTE === data.estado ||
                  ESTADO_VERIFICACION_EFECTO.COMUNICADO === data.estado
                    ? ""
                    : "!tw-text-neutral-75"
                }
                onSelectionChange={({
                  value,
                }: {
                  originalEvent: React.SyntheticEvent
                  value:
                    | GetFacturasAceptablesDtoProps[]
                    | GetPagaresAceptablesDtoProps[]
                  type?: "multiple" | "all" | "checkbox" | "row" | undefined
                }) => {
                  setSelection(value)
                }}
                size='small'
                loading={isLoading}
                emptyMessage={t("primeReact.table.emptyMessage")}
              />
            </div>
            <PaginationERP
              className={PAGINATION_STYLES}
              totalRecords={totalCount}
              first={pagination.skipCount}
              rows={pagination.itemsPerPage}
              resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
              setPagination={setPagination}
              optionDropdown={[10, 20, 50, 100]}
              texts={{
                textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
                textCurrentPageReport: `${t("pagination.from")}`,
              }}
            />
          </SecondaryValidacion>
        </div>

        <ModalAceptarFinanciacion state={location.state} />
      </div>
    </div>
  )
}

export default Financiacion
